import { createContext, useContext, type ReactNode } from 'react';
import type { MotionQuality } from '../../../../features/motion/quality';
import type { NarrationProviderCode } from '../constants';
import type { DroppedMaterial, Scenario, ScenarioResponse } from '../types';
import type { GenerationAsset } from '../../state/useGenerationState';
import type { MentionActorSource } from '../../state/mentionUtils';
import type { DialogueLine, SpeakerLine } from '../dialogueLines';
import type { MotionSyncStatusMap } from './useMotionSyncStatus';
import type { WholeMotionJobKind, WholeMotionRequestResult } from './useVideoHandlers';
import type { GenerateVoiceLinesOptions, VoiceLinesLastResult, VoiceLinesRequestResult, VoiceLinesRunning } from './useVoiceLineHandlers';
import type { SceneFrameSettingsApi } from './useSceneFrameSettings';
import type { SceneFrameReferenceCandidate } from '../utils/sceneFrameReferences';

export type SceneEditingField = {
  sceneId: string;
  field: 'script' | 'dialogue' | 'setting' | 'visual' | 'narration' | 'telop';
} | null;


/**
 * SceneItem 配下 (SceneFrameCarousel / SceneFrameTabs / SceneDetailColumn /
 * SceneFlowActions / SceneVideoBox / SceneNarrationBox) が参照する
 * 「シーン操作ハンドラ + 全シーン共通の参照データ」の供給口。
 *
 * 判断基準:
 * - シーンをまたいで同一の値 (ハンドラ・素材/クレジット/モデルのクエリ結果・
 *   sceneKey で引く共有マップ・setter・mutation・ref) → この Context
 * - その行だけの値 (scenario / scene / sceneIndex / sceneKey / isFirstScene …) → props
 *
 * 供給元は ScenarioGenerator の `sceneHandlersValue`。以前は WorkPanel が
 * `sceneItemBase` として同じ束をバケツリレーしていた。
 */
export interface SceneHandlersContextValue {
  // 開閉・ドラッグ状態 (sceneKey で引く共有マップ)
  expandedConfirmButton: string | null;
  expandedNarrations: Record<string, boolean>;
  expandedVideos: Record<string, boolean>;
  dragOverLastFrameKey: string | null;
  dragOverSceneKey: string | null;
  isGlobalDragging: boolean;
  isDraggingVideo: boolean;
  // 場面継続／場面転換 (continues_previous_shot) の PATCH が進行中のシーン (sceneKey)。
  // 保存完了まで該当シーンの動画・フレーム生成ボタンを非活性にし、保存と生成を直列化する
  // (docs/features/link-previous-scene-continuity-default-plan.md §9 step-1 項目7・FE補助)。
  pendingContinuityScenes: Set<string>;

  // Editing
  editingField: SceneEditingField;
  editingValue: string;
  deletingScenes: Set<string>;
  addingScenes: Set<string>;
  swappingScenes: boolean;

  // Settings
  videoSettings: { aspectRatio: string; resolution: string; duration: number; generateAudio: boolean; resizeMode: string };
  videoGenerationMode: string | null;
  // 素材 @メンション候補 (docs/features/material-at-mention-plan.md レビュー指摘 P1)。
  // selectedAssets のうち、動画生成の参照上限で実際には送信されない素材を除いたもの
  // (プロンプトモード基準の近似。ScenarioGenerator の mentionEligibleAssets 参照)。
  mentionEligibleAssets: GenerationAsset[];
  // AI俳優 @メンション候補 (同「俳優メンション」節)。casting/seedancePreset は排他で
  // 常に候補になる (参照上限フィルタ対象外。ScenarioGenerator の mentionActorSources 参照)。
  mentionActorSources: MentionActorSource[];
  // 動画生成の参照にキャラクターシート(人物画像)を含めるか。動画カードに表示するトグル。
  // キャラクター俳優の CS には適用しない (計画書 character-casting-consistency-plan.md
  // 修正3-b)。
  includeCastingSheet: boolean;
  // フルキャストに人間俳優が1人もいない場合は非表示にする (トグルが死にスイッチになるため。
  // 計画書 修正3-b)。
  showCastingSheetToggle: boolean;
  onIncludeCastingSheetChange: (next: boolean) => void;
  // 「人物の顔を描かない」モードか。Seedance + フレームモード + 顔含み のとき、
  // シーン1 の first_frame をユーザー画像で渡すと BytePlus face_upload_restricted で
  // 拒否されるため、シーン1 の first_frame 入力 UI を非活性化して動画を Seedance に
  // 作らせる挙動に切り替える判定に使う。
  avoidFaces: boolean;

  // Assets & product
  manualProductId: string | null;
  heroImageUrl: string | null;
  asin: string;

  // Credits
  creditBadgeText: string | number;
  isCreditInsufficient: boolean;
  isStorageExceeded: boolean;
  creditRatesData: any;
  creditsData: any;

  // Narration
  // アクティブシナリオの採用 BGM がボーカル主体 (is_vocal_lead) のとき true。
  // シーン単体の「このナレーションだけ生成」ボタンの無効化に使う
  // (docs/features/scenario-song-lyrics-bgm-plan.md §3.6/§5.5)。
  narrationBlockedByVocalLeadBgm: boolean;
  // 「英語で発音する」(三点メニュー)。サウンドロゴ等の英語ネイティブ読み用。シーン個別 (sceneKey で引く)
  narrationEnglishScenes: Record<string, boolean>;
  setNarrationEnglishScenes: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  selectedNarrationProvider: NarrationProviderCode;
  setSelectedNarrationProvider: React.Dispatch<React.SetStateAction<NarrationProviderCode>>;
  /** localStorage に保存せずに「音声AIモデル」を合わせる (voice-map のナレーターの声からの復元用) */
  restoreNarrationProvider?: (provider: NarrationProviderCode) => void;
  recommendedNarrator: any;
  // 方言付きシナリオのナレーターの地域 (Gemini の accent の API 生値)。'' は絞り込みなし
  narratorAccent: string;
  voicesData: any[] | undefined;
  /** ボイス一覧を取得中 (プロバイダ切替直後など。Gemini はサンプルの署名で時間がかかる) */
  voicesLoading?: boolean;
  voiceNameMap: Record<string, string>;

  // Materials data
  tempMaterials: any;
  cloudMaterials: any;
  downloadingMaterials: Set<string>;

  // Setters
  setExpandedNarrations: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setExpandedVideos: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  /** シーン単位のフレーム生成設定 (追加プロンプト等) の保存/flush/リセット (useSceneFrameSettings) */
  sceneFrameSettings: SceneFrameSettingsApi;
  /** 参照画像の候補 (生成と調整ダイアログのドライランで同じ入力を使う。useFrameHandlers) */
  collectSceneFrameReferenceCandidates: (
    scenario: Scenario,
    scene: Scenario['scenes'][number],
    frameType: 'first' | 'last',
    opts?: { additive?: boolean; firstFrame?: { id: string | null; url: string } | null; added?: ReadonlyArray<string> },
  ) => SceneFrameReferenceCandidate[];
  /** このシーンだけ追加した素材 (overrides.added) の URL / 名前を候補関数の前に引いておく */
  ensureAddedMaterialsResolved: (ids: ReadonlyArray<string>) => Promise<void>;
  /** 追加した素材のキャッシュを捨てる (一覧から外したとき。再追加で取り直す) */
  forgetAddedMaterial: (id: string) => void;
  setDragOverLastFrameKey: React.Dispatch<React.SetStateAction<string | null>>;
  setDragOverSceneKey: React.Dispatch<React.SetStateAction<string | null>>;
  setPendingContinuityScenes: React.Dispatch<React.SetStateAction<Set<string>>>;
  setEditingField: React.Dispatch<React.SetStateAction<SceneEditingField>>;
  setEditingValue: React.Dispatch<React.SetStateAction<string>>;
  setPreviewModalMaterial: React.Dispatch<React.SetStateAction<any>>;
  setPreviewModalSceneId: React.Dispatch<React.SetStateAction<string | null>>;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
  lastResult: any;

  // Handlers
  handleGenerateScenePreview: (
    scenario: Scenario,
    scene: Scenario['scenes'][number],
    opts?: { waitForCompletion?: boolean; silent?: boolean; additive?: boolean; notifyBatchId?: string },
  ) => void;
  handleConfirmFirstFrame: (sceneId: string, sceneKey: string, materialId: string) => void;
  handleClearFirstFrame: (sceneId: string, sceneKey: string, currentIndex?: number) => void;
  handleDropMaterial: (scenario: Scenario, scene: Scenario['scenes'][number], materialData: DroppedMaterial) => void;
  handleDropLastFrameMaterial: (scenario: Scenario, scene: Scenario['scenes'][number], materialData: DroppedMaterial) => void;
  handleRemoveLastFrame: (scenario: Scenario, scene: Scenario['scenes'][number]) => void;
  /** 最後のフレームを「ダイアログを開いたときの状態」(snapshot・null = 未設定) に戻す */
  handleRestoreLastFrame: (scenario: Scenario, scene: Scenario['scenes'][number], snapshot: { url: string; sourceUrl?: string; materialId: string; width?: number; height?: number } | null) => Promise<void>;
  handleGenerateLastFrame: (scenario: Scenario, scene: Scenario['scenes'][number], sceneKey: string) => void;
  handleGenerateSceneVideo: (
    scenario: Scenario,
    scene: Scenario['scenes'][number],
  ) => void;
  // 全編モーション Phase 2f (§10.6): 全編を 1 本で生成 / 全編の「AI で修正」/ 実行中のジョブ (シナリオ id → 種類)。
  // 未供給 (テスト等) なら全編の操作を出さない。
  /** quality = 送る品質モード (パネルがライトの料金の有無で選べる値に寄せたもの)。省略は全編の経路の選択 */
  handleGenerateWholeMotion?: (scenario: Scenario, quality?: MotionQuality) => Promise<WholeMotionRequestResult>;
  handleReviseWholeMotion?: (scenario: Scenario, instruction: string) => Promise<WholeMotionRequestResult>;
  wholeMotionRunning?: Record<string, WholeMotionJobKind>;
  /** 全編ジョブの現在の段階の文言 (シナリオ id →)。モーション専用モードのカードが出す */
  wholeMotionStage?: Record<string, string>;
  // 行ベースのナレーション (歌モノ以外・§10.7.3): ナレーションを生成 (sceneIds 省略で全シーン・options.sceneLanguages で
  // シーンごとの英語発音) / 1 行の撮り直し / 実行中のジョブ (シナリオ id →)。未供給 (テスト等) なら生成の操作を出さない。
  handleGenerateVoiceLines?: (
    scenario: Scenario,
    sceneIds?: string[],
    options?: GenerateVoiceLinesOptions,
  ) => Promise<VoiceLinesRequestResult>;
  handleRetakeVoiceLine?: (scenario: Scenario, sceneId: string, lineId: string) => Promise<VoiceLinesRequestResult>;
  /** 声の素材が画面の外で変わった後 (分割位置の調整) にシナリオを取り直し、声で変わる列を画面に写す */
  refreshVoiceScenario?: (scenarioId: string) => Promise<void>;
  voiceLinesRunning?: Record<string, VoiceLinesRunning>;
  // 直近の声のジョブの失敗したシーンの一覧と計画の advice (シナリオ id →)。声のセクションに残して出す
  voiceLinesLastResult?: Record<string, VoiceLinesLastResult>;
  handleSelectVideo: (sceneId: string, materialId: string, key: string, durationSeconds?: number, hasAudio?: boolean) => void;
  // 「採用しない」トグル (動画 / 前面音声)。素材紐付けは保持したまま合成・エクスポートから外す。
  handleToggleVideoExcluded: (sceneId: string, excluded: boolean) => void;
  handleToggleNarrationExcluded: (sceneId: string, excluded: boolean) => void;
  handleSelectNarration: (sceneId: string, materialId: string, voiceId?: string, durationSeconds?: number) => void;
  handleSetAudioTrackType: (sceneId: string, type: 'narration' | 'sound_logo') => void;
  handleSelectSoundLogo: (sceneId: string, materialId: string) => void;
  handleMergeComplete: (sceneId: string, key: string, materialId: string, durationSeconds?: number) => void;
  handleSaveField: (sceneId: string, field: 'script' | 'dialogue' | 'setting' | 'visual' | 'narration' | 'telop', value: string) => void;
  handleSwapSceneOrder: (scenarioId: string, sceneId1: string, sceneId2: string) => void;
  // 指定シーンの直後に空シーンを挿入する (ストーリーボードの3点メニュー「シーンを下に追加」)
  handleAddSceneBelow: (scenarioId: string, afterSceneId: string) => void;
  handleDeleteScene: (scenarioId: string, sceneId: string) => void;
  handleDownloadMaterial: (materialId: string, filename: string) => void;
  handleDropVideoMaterial: (scenario: Scenario, scene: Scenario['scenes'][number], materialData: DroppedMaterial) => void;

  // Utilities
  getSceneVideoSettings: (key: string) => any;
  updateSceneVideoSettings: (key: string, settings: any, opts?: { skipPersist?: boolean }) => void;
  // シナリオ既定の動画モデル/解像度の変更を、該当シナリオの全シーン override に一括反映する
  applyScenarioVideoSettingsToScenes: (scenarioId: string, settings: { videoModel?: string; resolution?: string }) => void;
  getSceneStoryboardDuration: (key: string, fallback?: number) => number;
  updateSceneStoryboardDuration: (key: string, seconds: number) => void;
  // BE が書き戻したコマ尺 (歌 → 映像同期) をローカル state に反映する (DB へは書かない)
  applySceneStoryboardDurations: (entries: Record<string, number>) => void;
  updateSceneBrandCutDuration: (sceneId: string, seconds: number) => void;
  getPreviewWidth: (aspectRatio: string) => number;
  openFrameDropdown: (id: string, triggerEl: HTMLElement) => void;
  openVideoModelDropdown: (id: string, triggerEl: HTMLElement) => void;
  openMaterialsPanel: (mode: 'select' | 'manage', contextData?: any) => void;
  openPurchaseModal: () => void;

  // Mutations
  updateSceneMutation: any;
  deleteSceneMutation: any;
  createSceneMutation: any;

  // Refs
  videoDropZoneRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  videoPlayButtonRefs: React.MutableRefObject<Map<string, HTMLButtonElement>>;
  // 構造化セリフ (dialogue_lines) 編集中の最新値ミラー ref。SceneDetailColumn が
  // 編集中だけ書き込み、useSceneActions の外側クリック dirty 判定が読む
  // (2026-08-17 要件2)。
  editingDialogueLinesRef: React.MutableRefObject<DialogueLine[]>;
  // 構造化テロップ (telop_lines) 編集中の最新値ミラー ref (行エディタ形。同上)。
  editingTelopLinesRef: React.MutableRefObject<SpeakerLine[]>;

  // Other
  fetchMaterialById: (id: string) => Promise<any>;
  /** スワイプ動画 (docs/features/swipe-motion-clip-plan.md §4.1): 静止画シーンの画像からスワイプ風の動画素材を作りシーンに差し込む (無料)。
   *  未供給 (テスト等) なら入口を出さない */
  handleGenerateSwipeClip?: (scenario: Scenario, scene: Scenario['scenes'][number], params: import('../../../../features/swipeClip/schema').SwipeClipParams) => Promise<void>;
  /** 絵コンテのモーション上書き (docs/features/brand-cut-motion-graphics-plan.md §10.13 4.): 本編シーンの区間だけ映像を上書きする
   *  モーションを定額で作る。未供給 (テスト等) なら生成ボタンを出さない */
  handleGenerateStoryboardMotionOverlay?: (scenario: Scenario, scene: Scenario['scenes'][number], opts: { mixSound: boolean; showTelop: boolean }) => Promise<void>;
  // 生成済みモーションシーンの BGM 同期の鮮度 (scenario_id → scene_id → {stale, reason}・useMotionSyncStatus)。
  // docs/features/brand-cut-motion-graphics-plan.md §15.4。未供給 (テスト等) なら注意を出さない。
  motionSyncStatus?: MotionSyncStatusMap;
}

const SceneHandlersContext = createContext<SceneHandlersContextValue | null>(null);

export function SceneHandlersProvider({
  value,
  children,
}: {
  value: SceneHandlersContextValue;
  children: ReactNode;
}) {
  return <SceneHandlersContext.Provider value={value}>{children}</SceneHandlersContext.Provider>;
}

export function useSceneHandlers(): SceneHandlersContextValue {
  const ctx = useContext(SceneHandlersContext);
  if (!ctx) {
    throw new Error('useSceneHandlers must be used inside <SceneHandlersProvider>');
  }
  return ctx;
}
